import type { LucideIcon } from 'lucide-react'
import {
  AlarmClockIcon,
  BookOpenIcon,
  ClipboardListIcon,
  KeyRoundIcon,
  LandmarkIcon,
  LayersIcon,
  PaletteIcon,
  PencilRulerIcon,
  SparklesIcon,
  UsersIcon,
} from 'lucide-react'

export type Stat = {
  value: string
  label: string
}

export type IconItem = {
  icon: LucideIcon
  text: string
}

export type PackItem = {
  icon: LucideIcon
  title: string
  text: string
}

export type PageSample = {
  src: string
  level: string
  title: string
  caption: string
}

export type Bonus = {
  title: string
  text: string
  was: string
  cover: string
}

export type Testimonial = {
  quote: string
  name: string
  location: string
}

export type Faq = {
  q: string
  a: string
}

export const stats: Stat[] = [
  { value: '10', label: 'Kompletnych Tomów' },
  { value: '500+', label: 'Stron Materiałów' },
  { value: '100%', label: 'Zgodne z Podstawą' },
  { value: '100%', label: 'Gotowe do Druku' },
]

export const differenceStats: Stat[] = [
  { value: '500+', label: 'Stron Treści' },
  { value: '4 → Matura', label: 'Od Klasy 4 do Matury' },
  { value: '100%', label: 'Praktycznych Rozwiązań' },
]

export const beforeItems: string[] = [
  'Godzinami szukasz inspiracji na Pinterest i w sieci',
  'Samodzielnie tworzysz karty pracy, które zajmują wieki',
  'Tracisz czas na przygotowywanie materiałów z historii sztuki',
  'Stresujesz się, czy zdążysz zrealizować podstawę programową',
  'Nie masz gotowych kryteriów oceniania pod ręką',
]

export const afterItems: string[] = [
  'Kompletny plan na każdy dział plastyki i historii sztuki',
  'Gotowe arkusze A4 z ćwiczeniami plastycznymi i instrukcjami',
  'Jasna progresja od technik podstawowych po analizę dzieła sztuki',
  'Wskazówki dla nauczyciela i gotowe scenariusze krok po kroku',
  'Klucze odpowiedzi i gotowe rubryki oceniania do każdego zadania',
]

export const recognizeItems: IconItem[] = [
  {
    icon: AlarmClockIcon,
    text: 'Tracisz niedziele na wymyślanie nowych projektów plastycznych',
  },
  {
    icon: SparklesIcon,
    text: 'Szukasz zadań, które są atrakcyjne wizualnie i angażują nawet „nieuzdolnionych” uczniów',
  },
  {
    icon: LandmarkIcon,
    text: 'Musisz szybko przygotować materiały do historii sztuki lub analizy dzieł',
  },
  {
    icon: UsersIcon,
    text: 'Pracujesz z uczniami o różnych poziomach i potrzebujesz zróżnicowanych wyzwań',
  },
  {
    icon: ClipboardListIcon,
    text: 'Chcesz mieć profesjonalne materiały, które wyróżnią Twoje lekcje w szkole',
  },
]

export const packItems: PackItem[] = [
  {
    icon: LayersIcon,
    title: '10 Kompletnych Tomów',
    text: 'Od podstaw rysunku po historię sztuki i przygotowanie do matury',
  },
  {
    icon: BookOpenIcon,
    title: 'Program 500+ stron',
    text: 'Uporządkowany tematycznie, w gotowym do druku formacie PDF A4',
  },
  {
    icon: PencilRulerIcon,
    title: 'Karty Pracy i Projekty',
    text: 'Gotowe do rozdania na lekcji, z miejscem na twórczość i teorię',
  },
  {
    icon: LandmarkIcon,
    title: 'Baza Wiedzy o Sztuce',
    text: 'Profesjonalne opracowania epok, artystów i technik plastycznych',
  },
  {
    icon: KeyRoundIcon,
    title: 'Testy i Quizy',
    text: 'Gotowe sprawdziany z wiedzy o plastyce z pełnym kluczem odpowiedzi',
  },
  {
    icon: PaletteIcon,
    title: 'Pomoce Wizualne',
    text: 'Grafiki, schematy kompozycji i koło barw gotowe do pokazania uczniom',
  },
]

export const pageSamples: PageSample[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/oPTLXCzzmTPRT5ZuywVun2/Captura_de_pantalla_2026-09-06_a_las_11.09.14_p.webp',
    level: 'Rysunek · Krok po kroku',
    title: 'Jak narysować twarz — krok po kroku',
    caption:
      'Czteroetapowa instrukcja rysowania twarzy, sześć pól na emocje i oznaczenie trudności w gwiazdkach.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/bq2PMezhvbuEF6GCWTf2E5/Captura_de_pantalla_2026-09-06_a_las_11.10.03_p.webp',
    level: 'Perspektywa · Aktywności',
    title: 'Perspektywa podstawowa',
    caption:
      'Trzy zadania o rosnącej trudności: linia horyzontu, punkt zbiegu i perspektywa własnej sali.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3sM4RdL23Pp5nrempKv1QD/Captura_de_pantalla_2026-09-06_a_las_11.11.56_p.webp',
    level: 'Architektura · Moduł 4',
    title: 'Analiza budynku i detale epok',
    caption:
      'Karta pracy z polami do wypełnienia, porównaniem gotyku, renesansu i baroku oraz własnym projektem.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/poYPATnu7kMBnyo2W3gLQr/Captura_de_pantalla_2026-09-06_a_las_11.12.42_p.webp',
    level: 'Renesans · Moduł 4',
    title: 'Analiza Mony Lisy i studium anatomii',
    caption:
      'Trzy aktywności z reprodukcjami mistrzów renesansu i pytaniami do analizy dzieła.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/fxUQyBe25hJQ8HbEFPuLcF/Captura_de_pantalla_2026-09-06_a_las_11.14.01_p.webp',
    level: 'Historia sztuki · Klasa 7–8',
    title: 'Karty do wycinania — epoki w sztuce',
    caption:
      'Gotowe flashcards z realizmem, impresjonizmem i postimpresjonizmem: artysta, rok i tytuł dzieła.',
  },
]

export const bonuses: Bonus[] = [
  {
    title: 'SZYBKI START NAUCZYCIELA™',
    text: 'Ebook z gotowymi scenariuszami na pierwsze lekcje plastyki',
    was: '50 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/2de70dd8-0f27-4b76-b192-1012f92a90cf.jpg',
  },
  {
    title: 'PLANER DRUKU I SEGREGATORÓW™',
    text: 'System organizacji fizycznych teczek nauczyciela',
    was: '50 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/04907dfa-4f0b-47f2-902c-54ffb46755d1.jpg',
  },
  {
    title: 'RUBRYKI EXPRESS™',
    text: 'Gotowe arkusze oceniania projektów plastycznych',
    was: '50 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/3779f090-8dd8-4f9e-9e91-5b6f8fba3b07.jpg',
  },
  {
    title: 'LEKCJE AWARYJNE™',
    text: 'Zbiór szybkich zadań 15-minutowych, gdy zabraknie czasu',
    was: '50 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/0a7aae81-1e4a-4990-b9fc-fa2ecd5ee8e1.jpg',
  },
  {
    title: 'KOMUNIKACJA Z RODZICAMI I DYREKCJĄ™',
    text: 'Szablony e-maili i sprawozdań z działań artystycznych',
    was: '50 zł',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/ae4ede59-e716-4272-bda7-2af7b3bb2447.jpg',
  },
]

export const offerRows: Array<{ label: string; value: string }> = [
  { label: '10 Kompletnych Tomów PDF', value: '399 zł' },
  { label: '5 Dodatków Ekskluzywnych', value: '250 zł' },
]

export const testimonials: Testimonial[] = [
  {
    quote:
      'Nareszcie! Moje niedziele odzyskały spokój. Plastyka przestała być chaosem, a stała się przyjemnością.',
    name: 'Marta K.',
    location: 'SP Poznań',
  },
  {
    quote:
      'Karty pracy są tak ładne, że uczniowie sami proszą o kolejne. Historia sztuki w końcu ich ciekawi.',
    name: 'Janusz P.',
    location: 'Liceum',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Jak otrzymam materiały?',
    a: 'Natychmiast po wpłacie na Twój e-mail trafi link do pobrania wszystkich plików PDF.',
  },
  {
    q: 'Czy mogę drukować materiały?',
    a: 'Tak! Wszystko jest w formacie A4, zoptymalizowane pod druk czarno-biały i kolorowy.',
  },
  {
    q: 'Czy to jest subskrypcja?',
    a: 'Nie. Płacisz tylko raz i masz dostęp do materiałów na zawsze.',
  },
]
